import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  opsApi, apiError, fmtINR, fmtNum, GRAD, FONT_HEAD, ROLE_LABEL, ROLE_RANK, ROLE_CREATES, INDIA_ZONES, Pagination,
} from './opsApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, Plus, MapPinned, UserX, UserCheck, KeyRound, GitBranch, Globe2, MapPin } from 'lucide-react';

// Tiny helper — pulls the zone label from the current user's scope_path.
// Mirrors the server's `zone_from_path` so a Zone Admin can see the right
// state list immediately without an extra round-trip.
const ZONE_BY_SLUG = { north: 'North', south: 'South', east: 'East', west: 'West' };
function zone_from_user(u) {
  const parts = (u?.scope_path || '').split('/').filter(Boolean);
  return ZONE_BY_SLUG[parts[1]] || u?.zone || '';
}

export default function OpsTeamOrg({ user }) {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [units, setUnits] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showUnit, setShowUnit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [statesByZone, setStatesByZone] = useState({});  // { Karnataka: [...] } cached per zone
  const rank = ROLE_RANK[user.role] || 0;
  // What role(s) can the current user create immediately below them?
  const creatableRoles = useMemo(() => ROLE_CREATES[user.role] || [], [user.role]);
  // First option as the default role
  const defaultRole = creatableRoles[0] || 'sales_rep';

  const [form, setForm] = useState({
    full_name: '', email: '', password: '', phone: '',
    role: defaultRole, target_monthly: '',
    // New fields:
    zone: '', state: '',
  });
  const [unitForm, setUnitForm] = useState({ name: '', kind: 'zone', parent_path: '/india' });

  const load = useCallback(() => {
    opsApi.get('/team', { params: { page, page_size: 50 } }).then((r) => setData(r.data)).catch(() => {});
    opsApi.get('/org-units').then((r) => setUnits(r.data)).catch(() => {});
  }, [page]);

  useEffect(() => { load(); }, [load]);

  // Fetch states for a zone the first time it's requested.
  const ensureStatesFor = useCallback(async (zone) => {
    if (!zone || statesByZone[zone]) return;
    try {
      const r = await opsApi.get('/states', { params: { zone } });
      setStatesByZone((m) => ({ ...m, [zone]: r.data.states || [] }));
    } catch (_e) { /* dropdown will simply stay empty */ }
  }, [statesByZone]);

  // Reset zone/state when the role changes — they're only relevant for specific role+creator pairs.
  const onRoleChange = (next) => {
    setForm((f) => ({ ...f, role: next, zone: '', state: '' }));
  };
  const onZoneChange = (next) => {
    setForm((f) => ({ ...f, zone: next, state: '' }));
    ensureStatesFor(next);
  };

  // What does the current form need from the user?
  // Super → Zone Admin: zone required
  // Zone  → State Manager: state required (zone inherited automatically)
  // State → Sales Rep: nothing extra
  const needsZone  = user.role === 'super_admin' && form.role === 'zone_admin';
  const needsState = user.role === 'zone_admin'  && form.role === 'state_manager';

  const addMember = async () => {
    if (!form.full_name || !form.email || !form.password) { toast.error('Name, email and password are required'); return; }
    if (needsZone && !form.zone) { toast.error('Pick a zone for the new Zone Admin'); return; }
    if (needsState && !form.state) { toast.error('Pick a state for the new State Manager'); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.target_monthly === '') delete payload.target_monthly;
      else payload.target_monthly = Number(payload.target_monthly);
      // Strip empty zone/state — backend infers them.
      if (!payload.zone) delete payload.zone;
      if (!payload.state) delete payload.state;
      await opsApi.post('/team', payload);
      toast.success('Team member created — they will set up 2FA on first login');
      setShowAdd(false);
      setForm({ full_name: '', email: '', password: '', phone: '',
                role: defaultRole, target_monthly: '', zone: '', state: '' });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const run = async (fn, success) => {
    try { await fn(); toast.success(success); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const addUnit = async () => {
    if (!unitForm.name.trim()) { toast.error('Name required'); return; }
    setBusy(true);
    try {
      await opsApi.post('/org-units', unitForm);
      toast.success('Org unit created — assign scope_paths to roll out the new tier');
      setShowUnit(false);
      setUnitForm({ name: '', kind: 'zone', parent_path: '/india' });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="ops-team-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" style={FONT_HEAD}>Team &amp; Org</h1>
          <p className="text-sm text-slate-500 mt-1">Desk-side accounts with role + geographic scope_path. 2FA is mandatory for everyone.</p>
        </div>
        <div className="flex gap-2">
          {rank >= 5 && (
            <Button data-testid="add-org-unit-btn" variant="outline" onClick={() => setShowUnit(true)} className="rounded-xl h-10">
              <MapPinned className="w-4 h-4 mr-1.5" /> New org unit
            </Button>
          )}
          {creatableRoles.length > 0 && (
            <Button data-testid="add-member-btn" onClick={() => setShowAdd(true)} className="text-white rounded-xl h-10 border-0" style={{ background: GRAD }}>
              <Plus className="w-4 h-4 mr-1.5" /> Add team member
            </Button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 text-sm font-semibold flex items-center gap-2" style={FONT_HEAD}>
          <Users className="w-4 h-4 text-[#34A853]" /> Team ({data ? fmtNum(data.total) : '…'})
        </div>
        {data && (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-xs text-slate-400 uppercase tracking-wide">
                  <th className="px-5 py-3 font-medium">Member</th>
                  <th className="px-4 py-3 font-medium">Role</th>
                  <th className="px-4 py-3 font-medium">Zone / State</th>
                  <th className="px-4 py-3 font-medium">Scope</th>
                  <th className="px-4 py-3 font-medium text-right">Institutions</th>
                  <th className="px-4 py-3 font-medium text-right">Pending comm.</th>
                  <th className="px-4 py-3 font-medium">2FA</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((m) => (
                  <tr key={m.id} data-testid={`team-row-${m.email}`} className="border-b border-slate-50">
                    <td className="px-5 py-3.5">
                      <div className="font-medium text-slate-800">{m.full_name} <span className="text-[10px] text-slate-400 font-mono ml-1">{m.employee_code}</span></div>
                      <div className="text-xs text-slate-400">{m.email}{m.phone ? ` · ${m.phone}` : ''}</div>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${m.role === 'super_admin' ? 'text-white' : 'bg-green-50 text-green-700'}`}
                        style={m.role === 'super_admin' ? { background: GRAD } : undefined}>
                        {ROLE_LABEL[m.role]}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-[11px]" data-testid={`team-row-zone-${m.email}`}>
                      <div className="flex flex-col gap-0.5">
                        {m.zone && (
                          <span className="inline-flex items-center gap-1 font-bold text-[#0d9488]">
                            <Globe2 className="w-2.5 h-2.5" /> {m.zone}
                          </span>
                        )}
                        {m.state && (
                          <span className="inline-flex items-center gap-1 text-slate-500">
                            <MapPin className="w-2.5 h-2.5" /> {m.state}
                          </span>
                        )}
                        {!m.zone && !m.state && <span className="text-slate-300">—</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-[11px] font-mono text-[#3B82F6]">{m.scope_path}</td>
                    <td className="px-4 py-3.5 text-right tabular-nums">{m.institution_count}</td>
                    <td className="px-4 py-3.5 text-right tabular-nums font-medium text-amber-600">{fmtINR(m.pending_commission)}</td>
                    <td className="px-4 py-3.5">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${m.totp_enabled ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                        {m.totp_enabled ? 'enrolled' : 'pending setup'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${m.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>{m.status}</span>
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      {m.id !== user.id && rank > (ROLE_RANK[m.role] || 0) && (
                        <div className="flex justify-end gap-1.5">
                          {m.totp_enabled && (
                            <Button size="sm" variant="outline" data-testid={`reset-2fa-${m.email}`}
                              onClick={() => run(() => opsApi.put(`/team/${m.id}`, { reset_2fa: true }), '2FA reset — they re-enroll on next login')}
                              className="h-7 text-xs rounded-lg" title="Reset 2FA">
                              <KeyRound className="w-3 h-3" />
                            </Button>
                          )}
                          <Button size="sm" variant="outline" data-testid={`toggle-status-${m.email}`}
                            onClick={() => run(() => opsApi.put(`/team/${m.id}`, { status: m.status === 'active' ? 'inactive' : 'active' }),
                              m.status === 'active' ? 'Member deactivated' : 'Member reactivated')}
                            className={`h-7 text-xs rounded-lg ${m.status === 'active' ? 'text-red-600 border-red-200' : 'text-green-700 border-green-200'}`}>
                            {m.status === 'active' ? <><UserX className="w-3 h-3 mr-1" />Deactivate</> : <><UserCheck className="w-3 h-3 mr-1" />Reactivate</>}
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination data={data} onPage={setPage} />
          </>
        )}
      </div>

      {/* Org units */}
      <div>
        <h2 className="text-base font-semibold mb-3 flex items-center gap-2" style={FONT_HEAD}>
          <GitBranch className="w-4 h-4 text-[#3B82F6]" /> Geographic org tree
        </h2>
        <p className="text-xs text-slate-400 mb-4">Flat deployment today (everything under /india). Adding zone/state tiers later is a config change — create the unit, then point accounts &amp; institutions at the new scope_path. No migration needed.</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {units.map((u) => (
            <div key={u.id} data-testid={`org-unit-${u.path}`} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="h-1" style={{ background: GRAD }} />
              <div className="p-5">
                <div className="text-sm font-semibold" style={FONT_HEAD}>{u.name}</div>
                <div className="text-[10px] uppercase tracking-wide text-slate-400 mt-0.5">{u.kind}</div>
                <div className="text-[11px] font-mono text-[#3B82F6] mt-1">{u.path}</div>
                <div className="text-xs text-slate-500 mt-2 tabular-nums">{u.team_count} team · {u.institution_count} institutions</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add member */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={FONT_HEAD}>Add team member</DialogTitle>
            <DialogDescription>They log in at <b>/sales/login</b> and must enroll in 2FA (authenticator app) on first sign-in.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Full name *</Label>
                <Input data-testid="member-name-input" value={form.full_name} onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Work email *</Label>
              <Input data-testid="member-email-input" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Password *</Label>
              <Input data-testid="member-password-input" type="text" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} className="mt-1" placeholder="Temporary password" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Role</Label>
                <Select value={form.role} onValueChange={onRoleChange}>
                  <SelectTrigger className="mt-1" data-testid="member-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {creatableRoles.length === 0 && (
                      <div className="px-3 py-2 text-xs text-slate-500">Your role can&apos;t create users.</div>
                    )}
                    {creatableRoles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {needsZone && (
                <div>
                  <Label className="text-xs flex items-center gap-1"><Globe2 className="w-3 h-3" /> Zone *</Label>
                  <Select value={form.zone} onValueChange={onZoneChange}>
                    <SelectTrigger className="mt-1" data-testid="member-zone-select">
                      <SelectValue placeholder="Pick a zone" />
                    </SelectTrigger>
                    <SelectContent>
                      {INDIA_ZONES.map((z) => <SelectItem key={z} value={z} data-testid={`zone-option-${z.toLowerCase()}`}>{z}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {needsState && (
                <div>
                  <Label className="text-xs flex items-center gap-1"><MapPin className="w-3 h-3" /> State *</Label>
                  <Select
                    value={form.state}
                    onValueChange={(v) => setForm((f) => ({ ...f, state: v }))}
                    // Pre-fetch states for the manager's zone the moment the
                    // dropdown opens — no extra mount-time API call.
                    onOpenChange={(o) => { if (o) ensureStatesFor(zone_from_user(user)); }}>
                    <SelectTrigger className="mt-1" data-testid="member-state-select">
                      <SelectValue placeholder="Pick a state" />
                    </SelectTrigger>
                    <SelectContent>
                      {(statesByZone[zone_from_user(user)] || []).map((s) => (
                        <SelectItem key={s.id} value={s.name} data-testid={`state-option-${s.slug}`}>{s.name}</SelectItem>
                      ))}
                      {(statesByZone[zone_from_user(user)] || []).length === 0 && (
                        <div className="px-3 py-2 text-xs text-slate-500">Loading states…</div>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {!needsZone && !needsState && (
                <div>
                  <Label className="text-xs">Scope</Label>
                  <div className="mt-1 h-9 px-3 text-[12px] rounded-md border border-slate-200 bg-slate-50 flex items-center text-slate-600 font-mono"
                    data-testid="member-scope-inherited">
                    Inherited · {user.scope_path}
                  </div>
                </div>
              )}
            </div>
            {form.role === 'sales_rep' && (
              <div>
                <Label className="text-xs">Monthly revenue target (₹)</Label>
                <Input data-testid="member-target-input" type="number" min="0" value={form.target_monthly}
                  onChange={(e) => setForm((f) => ({ ...f, target_monthly: e.target.value }))} className="mt-1" placeholder="Default from settings" />
              </div>
            )}
            <Button data-testid="member-submit-btn" onClick={addMember} disabled={busy} className="w-full text-white rounded-xl h-10 border-0" style={{ background: GRAD }}>
              {busy ? 'Creating…' : 'Create member'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add org unit */}
      <Dialog open={showUnit} onOpenChange={setShowUnit}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={FONT_HEAD}>New org unit</DialogTitle>
            <DialogDescription>Extends the geographic tree (e.g. South Zone under /india). Config only — no data migration.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input data-testid="unit-name-input" value={unitForm.name} onChange={(e) => setUnitForm((f) => ({ ...f, name: e.target.value }))} placeholder="Name, e.g. South" />
            <Select value={unitForm.kind} onValueChange={(v) => setUnitForm((f) => ({ ...f, kind: v }))}>
              <SelectTrigger data-testid="unit-kind-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['zone', 'state', 'city', 'territory'].map((k) => <SelectItem key={k} value={k} className="capitalize">{k}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={unitForm.parent_path} onValueChange={(v) => setUnitForm((f) => ({ ...f, parent_path: v }))}>
              <SelectTrigger data-testid="unit-parent-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {units.map((u) => <SelectItem key={u.path} value={u.path}>{u.path}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button data-testid="unit-submit-btn" onClick={addUnit} disabled={busy} className="w-full text-white rounded-xl border-0" style={{ background: GRAD }}>
              {busy ? 'Creating…' : 'Create org unit'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
